import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [languageOpen, setLanguageOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(isNL ? 'Nederlands' : 'English');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<{ [key: string]: string }>({
    hardware: 'computing',
    software: 'business-software',
    'it-services': 'core-services',
    'generative-ai': 'ai-consulting',
  });

  const headerRef = useRef<HTMLDivElement>(null);

  const handleCategoryChange = (dropdown: string, category: string) => {
    setActiveCategory(prev => ({ ...prev, [dropdown]: category }));
  };

  const toggleDropdown = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const toggleMobileDropdown = (dropdownName: string) => {
    setActiveMobileDropdown(activeMobileDropdown === dropdownName ? null : dropdownName);
  };

  const switchLanguage = (toLang: 'en' | 'nl') => {
    const currentPath = location.pathname;
    let newPath: string;
    if (toLang === 'nl') {
      if (!currentPath.startsWith('/nl')) {
        newPath = currentPath === '/' ? '/nl' : `/nl${currentPath}`;
      } else {
        newPath = currentPath;
      }
      setSelectedLanguage('Nederlands');
    } else {
      if (currentPath.startsWith('/nl')) {
        newPath = currentPath.replace(/^\/nl/, '') || '/';
      } else {
        newPath = currentPath;
      }
      setSelectedLanguage('English');
    }
    setLanguageOpen(false);
    navigate(newPath);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (headerRef.current && !headerRef.current.contains(target)) {
        setLanguageOpen(false);
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header ref={headerRef} className="bg-white shadow-sm sticky top-0 z-50">
      <div className="py-3 border-b border-gray-200" style={{ background: 'linear-gradient(135deg, #0066CC 0%, #0052A3 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4 md:gap-8">
              <div className="hidden md:block text-sm text-blue-100">
                <span>{isNL ? 'Uw betrouwbare IT-partner sinds 2025' : 'Your trusted IT partner since 2025'}</span>
              </div>
            </div>
            <div className="relative language-selector">
              <button
                onClick={() => setLanguageOpen(!languageOpen)}
                className="flex items-center text-xs md:text-sm text-white hover:text-blue-100 transition-colors font-semibold"
              >
                <span>{selectedLanguage}</span>
                <div className="w-4 md:w-5 h-4 md:h-5 flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              {languageOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg py-2 z-[9999] border border-gray-200">
                  <button
                    onClick={() => switchLanguage('en')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-3 rounded-sm overflow-hidden">
                      <img src="https://flagcdn.com/w40/us.png" alt="US Flag" className="w-full h-full object-cover" />
                    </div>
                    English
                  </button>
                  <button
                    onClick={() => switchLanguage('nl')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-3 rounded-sm overflow-hidden">
                      <img src="https://flagcdn.com/w40/nl.png" alt="NL Flag" className="w-full h-full object-cover" />
                    </div>
                    Nederlands
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <a href={prefix || '/'} className="flex items-center hover:opacity-90 transition-opacity">
              <img
                src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/82e36469ca5f3882b92dbde3287cf9e4.jfif"
                alt="Thyamis Logo"
                className="h-12 md:h-16 w-auto"
              />
            </a>
          </div>
          
          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-6">
            <a href="#" className="flex items-center text-gray-600 hover:text-primary transition-colors">
              <div className="w-5 h-5 flex items-center justify-center mr-1">
                <i className="ri-user-line"></i>
              </div>
              <span className="text-sm">{isNL ? 'Account' : 'Account'}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-800 hover:text-primary transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block py-3 border-t border-gray-100">
          <ul className="flex space-x-8">
            <li className="relative group dropdown-menu">
              <button 
                onClick={() => toggleDropdown('hardware')}
                className="text-gray-800 hover:text-primary font-medium flex items-center cursor-pointer"
              >
                Hardware
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              {activeDropdown === 'hardware' && (
                <div 
                  className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-[800px] flex gap-6 z-50"
                >
                  <div className="w-48 border-r border-gray-200 pr-4">
                    <button
                      onClick={() => handleCategoryChange('hardware', 'computing')}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-2 flex items-center transition-colors cursor-pointer ${
                        activeCategory.hardware === 'computing' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-computer-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Computers' : 'Computing'}</span>
                    </button>
                    <button
                      onClick={() => handleCategoryChange('hardware', 'networking')}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors cursor-pointer ${
                        activeCategory.hardware === 'networking' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-router-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Netwerken' : 'Networking'}</span>
                    </button>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    {activeCategory.hardware === 'computing' && (
                      <>
                        <Link
                          to={`${prefix}/products/laptops-desktops`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              alt={isNL ? 'Laptops & Desktops' : 'Laptops & Desktops'}
                              className="w-full h-full object-cover"
                              src="https://public.readdy.ai/ai/img_res/670d73cc9848c269923fcc87ffe35257.jpg"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Laptops & Desktops' : 'Laptops & Desktops'}</h3>
                        </Link>
                        <Link
                          to={`${prefix}/products/servers-storage`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              alt={isNL ? 'Servers & Opslag' : 'Servers & Storage'}
                              className="w-full h-full object-cover"
                              src="https://readdy.ai/api/search-image?query=Professional%20enterprise%20server%20rack%20unit%20on%20clean%20white%20background%2C%20modern%20server%20hardware%20with%20blue%20LED%20indicators%20and%20premium%20build%20quality%20for%20business%20networks&width=240&height=240&seq=server1&orientation=squarish"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Servers & Opslag' : 'Servers & Storage'}</h3>
                        </Link>
                        <Link
                          to={`${prefix}/products/workstations`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20enterprise%20workstation%20computer%20on%20clean%20white%20background%2C%20high-performance%20workstation%20with%20modern%20design%20and%20premium%20build%20quality&width=240&height=240&seq=workstation1&orientation=squarish"
                              alt={isNL ? 'Werkstations' : 'Workstations'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Werkstations' : 'Workstations'}</h3>
                        </Link>
                        <Link
                          to={`${prefix}/products/components`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20computer%20components%20including%20RAM%20memory%20modules%2C%20graphics%20cards%2C%20and%20processors%20on%20clean%20white%20background%2C%20modern%20PC%20hardware%20components%20with%20premium%20build%20quality%20for%20enterprise%20systems&width=240&height=240&seq=components1&orientation=squarish"
                              alt={isNL ? 'Componenten' : 'Components'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Componenten' : 'Components'}</h3>
                        </Link>
                      </>
                    )}
                    {activeCategory.hardware === 'networking' && (
                      <>
                        <Link
                          to={`${prefix}/products/routers-switches`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20enterprise%20routers%20and%20network%20switches%20on%20clean%20white%20background%2C%20modern%20networking%20hardware%20with%20LED%20indicators%20and%20premium%20build%20quality%20for%20business%20networks&width=240&height=240&seq=routers1&orientation=squarish"
                              alt={isNL ? 'Routers & Switches' : 'Routers & Switches'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Routers & Switches' : 'Routers & Switches'}</h3>
                          <p className="text-sm text-gray-600 mt-1">{isNL ? 'Netwerkinfrastructuur' : 'Network infrastructure'}</p>
                        </Link>
                        <Link
                          to={`${prefix}/products/wireless-solutions`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20enterprise%20wireless%20access%20points%20and%20WiFi%20equipment%20on%20clean%20white%20background%2C%20modern%20wireless%20networking%20hardware%20with%20sleek%20design%20for%20business%20connectivity&width=240&height=240&seq=wireless1&orientation=squarish"
                              alt={isNL ? 'Draadloze Oplossingen' : 'Wireless Solutions'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Draadloze Oplossingen' : 'Wireless Solutions'}</h3>
                          <p className="text-sm text-gray-600 mt-1">{isNL ? 'WiFi en draadloze systemen' : 'WiFi and wireless systems'}</p>
                        </Link>
                        <Link
                          to={`${prefix}/products/security-appliances`}
                          onClick={closeDropdown}
                          className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20enterprise%20network%20security%20appliances%20and%20firewalls%20on%20clean%20white%20background%2C%20modern%20cybersecurity%20hardware%20with%20robust%20design%20for%20business%20protection&width=240&height=240&seq=security1&orientation=squarish"
                              alt={isNL ? 'Beveiligingsapparatuur' : 'Security Appliances'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Beveiligingsapparatuur' : 'Security Appliances'}</h3>
                          <p className="text-sm text-gray-600 mt-1">{isNL ? 'Netwerkbeveiligingshardware' : 'Network security hardware'}</p>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              )}
            </li>

            <li className="relative group dropdown-menu">
              <button 
                onClick={() => toggleDropdown('software')}
                className="text-gray-800 hover:text-primary font-medium flex items-center cursor-pointer"
              >
                Software
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              {activeDropdown === 'software' && (
                <div 
                  className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-[800px] flex gap-6 z-50"
                >
                  <div className="w-48 border-r border-gray-200 pr-4">
                    <button
                      onClick={() => handleCategoryChange('software', 'business-software')}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-2 flex items-center transition-colors cursor-pointer ${
                        activeCategory.software === 'business-software' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-briefcase-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Bedrijfssoftware' : 'Business Software'}</span>
                    </button>
                    <button
                      onClick={() => handleCategoryChange('software', 'security-utilities')}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors cursor-pointer ${
                        activeCategory.software === 'security-utilities' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-shield-check-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Beveiliging & Hulpprogramma\'s' : 'Security & Utilities'}</span>
                    </button>
                  </div>
                  <div className="flex-1">
                    {activeCategory.software === 'business-software' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Link to={`${prefix}/microsoft-365`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Microsoft%20365%20software%20package%20with%20clean%20office%20productivity%20icons%20on%20white%20background%2C%20modern%20business%20software%20suite%20with%20premium%20build%20quality&width=240&height=240&seq=ms365&orientation=squarish"
                              alt="Microsoft 365"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">Microsoft 365</h3>
                        </Link>
                        <Link to={`${prefix}/adobe-creative-cloud`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Adobe%20Creative%20Cloud%20software%20package%20with%20creative%20design%20tools%20on%20white%20background%2C%20modern%20creative%20software%20suite%20with%20premium%20build%20quality&width=240&height=240&seq=adobe&orientation=squarish"
                              alt="Adobe Creative Cloud"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">Adobe Creative Cloud</h3>
                        </Link>
                      </div>
                    )}
                    {activeCategory.software === 'security-utilities' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Link to={`${prefix}/antivirus-security`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Enterprise%20antivirus%20and%20security%20software%20with%20shield%20protection%20icons%20on%20white%20background%2C%20modern%20cybersecurity%20software%20with%20premium%20build%20quality&width=240&height=240&seq=antivirus&orientation=squarish"
                              alt={isNL ? 'Antivirus & Beveiliging' : 'Antivirus & Security'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Antivirus & Beveiliging' : 'Antivirus &amp; Security'}</h3>
                        </Link>
                        <Link to={`${prefix}/operating-systems`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Operating%20systems%20software%20including%20Windows%2C%20Linux%2C%20and%20macOS%20interfaces%20on%20white%20background%2C%20modern%20operating%20system%20software%20with%20premium%20build%20quality&width=240&height=240&seq=os&orientation=squarish"
                              alt={isNL ? 'Besturingssystemen' : 'Operating Systems'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Besturingssystemen' : 'Operating Systems'}</h3>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>

            <li className="relative group dropdown-menu">
              <button 
                onClick={() => toggleDropdown('it-services')}
                className="text-gray-800 hover:text-primary font-medium flex items-center cursor-pointer"
              >
                {isNL ? 'IT-diensten' : 'IT Services'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              {activeDropdown === 'it-services' && (
                <div 
                  className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-[700px] flex gap-6 z-50"
                >
                  <div className="w-48 border-r border-gray-200 pr-4">
                    <button
                      onClick={() => handleCategoryChange('it-services', 'core-services')}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-2 flex items-center transition-colors cursor-pointer ${
                        activeCategory['it-services'] === 'core-services' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-settings-3-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Kerndiensten' : 'Core Services'}</span>
                    </button>
                    <button
                      onClick={() => handleCategoryChange('it-services', 'managed-services')}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors cursor-pointer ${
                        activeCategory['it-services'] === 'managed-services' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-shield-star-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'Beheerde Diensten' : 'Managed Services'}</span>
                    </button>
                  </div>
                  <div className="flex-1">
                    {activeCategory['it-services'] === 'core-services' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Link to={`${prefix}/integration-configuration`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=IT%20integration%20and%20configuration%20services%20with%20network%20equipment%20and%20server%20setup%20on%20white%20background%2C%20modern%20IT%20service%20delivery%20with%20premium%20build%20quality&width=240&height=240&seq=integration&orientation=squarish"
                              alt={isNL ? 'Integratie & Configuratie' : 'Integration & Configuration'}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Integratie & Configuratie' : 'Integration &amp; Configuration'}</h3>
                        </Link>
                        <Link to={`${prefix}/cloud-services`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20cloud%20services%20with%20modern%20cloud%20infrastructure%20and%20data%20center%20visualization%20on%20white%20background%2C%20enterprise%20cloud%20solutions%20with%20premium%20build%20quality&width=240&height=240&seq=cloudservices&orientation=squarish"
                              alt={isNL ? 'Clouddiensten' : 'Cloud Services'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Clouddiensten' : 'Cloud Services'}</h3>
                        </Link>
                        <Link to={`${prefix}/it-consulting`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Professional%20IT%20consulting%20services%20with%20business%20analysts%20and%20technology%20experts%20in%20modern%20office%20environment%20on%20white%20background%2C%20enterprise%20consulting%20with%20premium%20build%20quality&width=240&height=240&seq=consultinging&orientation=squarish"
                              alt={isNL ? 'IT-advies' : 'IT Consulting'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'IT-advies' : 'IT Consulting'}</h3>
                        </Link>
                      </div>
                    )}
                    {activeCategory['it-services'] === 'managed-services' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Link to={`${prefix}/managed-it-services`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Managed%20IT%20services%20with%20professional%20technicians%20monitoring%20servers%20and%20network%20infrastructure%20in%20modern%20data%20center%20on%20white%20background%2C%20enterprise%20managed%20services%20with%20premium%20build%20quality&width=240&height=240&seq=managedIT&orientation=squarish"
                              alt={isNL ? 'Beheerde IT-diensten' : 'Managed IT Services'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'Beheerde IT-diensten' : 'Managed IT Services'}</h3>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>

            <li className="relative group dropdown-menu">
              <button 
                onClick={() => toggleDropdown('generativeAI')}
                className="text-gray-800 hover:text-primary font-medium flex items-center cursor-pointer"
              >
                {isNL ? 'Generatieve AI' : 'Generative AI'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              
              {activeDropdown === 'generativeAI' && (
                <div 
                  className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-[700px] flex gap-6 z-50"
                >
                  <div className="w-48 border-r border-gray-200 pr-4">
                    <button
                      onClick={() => handleCategoryChange('generative-ai', 'ai-consulting')}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-2 flex items-center transition-colors cursor-pointer ${
                        activeCategory['generative-ai'] === 'ai-consulting' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-brain-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'AI-advies' : 'AI Consulting'}</span>
                    </button>
                    <button
                      onClick={() => handleCategoryChange('generative-ai', 'ai-development')}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors cursor-pointer ${
                        activeCategory['generative-ai'] === 'ai-development' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-code-s-slash-line"></i>
                      </div>
                      <span className="font-medium">{isNL ? 'AI-oplossingen' : 'AI Solutions'}</span>
                    </button>
                  </div>
                  <div className="flex-1">
                    {activeCategory['generative-ai'] === 'ai-consulting' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Link to={`${prefix}/ai-consulting`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/a6bb57de9b881653888bbaa48b9fd053.png"
                              alt={isNL ? 'AI-advies' : 'AI Consulting'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">{isNL ? 'AI-advies' : 'AI Consulting'}</h3>
                        </Link>
                        <Link to={`${prefix}/machine-learning`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Machine%20learning%20training%20and%20optimization%20services%20with%20data%20analysis%20and%20algorithm%20development%20on%20white%20background%2C%20enterprise%20machine%20learning%20with%20premium%20build%20quality&width=240&height=240&seq=mltraining&orientation=squarish"
                              alt="Machine Learning"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800">Machine Learning</h3>
                        </Link>
                      </div>
                    )}
                    {activeCategory['generative-ai'] === 'ai-development' && (
                      <div className="grid grid-cols-3 gap-4">
                        <Link to={`${prefix}/ai-automation-integration`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=AI%20automation%20and%20integration%20systems%20with%20robotic%20process%20automation%20and%20workflow%20optimization%20on%20white%20background%2C%20enterprise%20AI%20automation%20with%20premium%20build%20quality&width=240&height=240&seq=aiautomation&orientation=squarish"
                              alt={isNL ? 'AI-automatisering & Integratie' : 'AI Automation & Integration'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{isNL ? 'AI-automatisering & Integratie' : 'AI Automation & Integration'}</h3>
                        </Link>
                        <Link to={`${prefix}/computer-vision`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://readdy.ai/api/search-image?query=Computer%20vision%20technology%20with%20image%20recognition%20and%20object%20detection%20systems%20on%20white%20background%2C%20enterprise%20computer%20vision%20with%20premium%20build%20quality&width=240&height=240&seq=computervision&orientation=squarish"
                              alt={isNL ? 'Computer Vision Oplossingen' : 'Computer Vision Solutions'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{isNL ? 'Computer Vision Oplossingen' : 'Computer Vision Solutions'}</h3>
                        </Link>
                        <Link to={`${prefix}/conversational-ai`} onClick={closeDropdown} className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-full h-24 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/29e525b4cf70f0120d353622f560e7af.jpeg"
                              alt={isNL ? 'Conversationele AI-systemen' : 'Conversational AI Systems'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{isNL ? 'Conversationele AI-systemen' : 'Conversational AI Systems'}</h3>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>

            <li>
              <Link to={`${prefix}/support`} className="text-gray-800 hover:text-primary font-medium">
                {isNL ? 'Ondersteuning' : 'Support'}
              </Link>
            </li>
            <li>
              <Link to={`${prefix}/about`} className="text-gray-800 hover:text-primary font-medium">
                {isNL ? 'Over Ons' : 'About'}
              </Link>
            </li>
            <li>
              <Link to={`${prefix}/contact`} className="text-gray-800 hover:text-primary font-medium">
                Contact
              </Link>
            </li>
          </ul>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            <nav className="space-y-2">
              {/* Hardware */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('hardware')}
                  className="w-full flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <span className="font-medium">Hardware</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-${activeMobileDropdown === 'hardware' ? 'up' : 'down'}-s-line`}></i>
                  </div>
                </button>
                {activeMobileDropdown === 'hardware' && (
                  <div className="pl-4 space-y-1 mt-2">
                    <div className="font-medium text-xs text-gray-500 px-4 py-2">{isNL ? 'Computers' : 'Computing'}</div>
                    <Link to={`${prefix}/products/laptops-desktops`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      Laptops & Desktops
                    </Link>
                    <Link to={`${prefix}/products/servers-storage`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Servers & Opslag' : 'Servers & Storage'}
                    </Link>
                    <Link to={`${prefix}/products/workstations`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Werkstations' : 'Workstations'}
                    </Link>
                    <Link to={`${prefix}/products/components`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Componenten' : 'Components'}
                    </Link>
                    <div className="font-medium text-xs text-gray-500 px-4 py-2 mt-2">{isNL ? 'Netwerken' : 'Networking'}</div>
                    <Link to={`${prefix}/products/routers-switches`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      Routers & Switches
                    </Link>
                    <Link to={`${prefix}/products/wireless-solutions`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Draadloze Oplossingen' : 'Wireless Solutions'}
                    </Link>
                    <Link to={`${prefix}/products/security-appliances`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Beveiligingsapparatuur' : 'Security Appliances'}
                    </Link>
                  </div>
                )}
              </div>

              {/* Software */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('software')}
                  className="w-full flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <span className="font-medium">Software</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-${activeMobileDropdown === 'software' ? 'up' : 'down'}-s-line`}></i>
                  </div>
                </button>
                {activeMobileDropdown === 'software' && (
                  <div className="pl-4 space-y-1 mt-2">
                    <div className="font-medium text-xs text-gray-500 px-4 py-2">{isNL ? 'Bedrijfssoftware' : 'Business Software'}</div>
                    <Link to={`${prefix}/microsoft-365`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      Microsoft 365
                    </Link>
                    <Link to={`${prefix}/adobe-creative-cloud`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      Adobe Creative Cloud
                    </Link>
                    <div className="font-medium text-xs text-gray-500 px-4 py-2 mt-2">{isNL ? 'Beveiliging & Hulpprogramma\'s' : 'Security & Utilities'}</div>
                    <Link to={`${prefix}/antivirus-security`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Antivirus & Beveiliging' : 'Antivirus & Security'}
                    </Link>
                    <Link to={`${prefix}/operating-systems`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Besturingssystemen' : 'Operating Systems'}
                    </Link>
                  </div>
                )}
              </div>

              {/* IT Services */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('it-services')}
                  className="w-full flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <span className="font-medium">{isNL ? 'IT-diensten' : 'IT Services'}</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-${activeMobileDropdown === 'it-services' ? 'up' : 'down'}-s-line`}></i>
                  </div>
                </button>
                {activeMobileDropdown === 'it-services' && (
                  <div className="pl-4 space-y-1 mt-2">
                    <div className="font-medium text-xs text-gray-500 px-4 py-2">{isNL ? 'Kerndiensten' : 'Core Services'}</div>
                    <Link to={`${prefix}/integration-configuration`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Integratie & Configuratie' : 'Integration & Configuration'}
                    </Link>
                    <Link to={`${prefix}/cloud-services`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Clouddiensten' : 'Cloud Services'}
                    </Link>
                    <Link to={`${prefix}/it-consulting`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'IT-advies' : 'IT Consulting'}
                    </Link>
                    <div className="font-medium text-xs text-gray-500 px-4 py-2 mt-2">{isNL ? 'Beheerde Diensten' : 'Managed Services'}</div>
                    <Link to={`${prefix}/managed-it-services`} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Beheerde IT-diensten' : 'Managed IT Services'}
                    </Link>
                  </div>
                )}
              </div>

              {/* Generative AI */}
              <div>
                <button
                  onClick={() => toggleMobileDropdown('generativeAI')}
                  className="w-full flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <span className="font-medium">{isNL ? 'Generatieve AI' : 'Generative AI'}</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-${activeMobileDropdown === 'generativeAI' ? 'up' : 'down'}-s-line`}></i>
                  </div>
                </button>
                {activeMobileDropdown === 'generativeAI' && (
                  <div className="pl-4 space-y-1 mt-2">
                    <div className="font-medium text-xs text-gray-500 px-4 py-2">{isNL ? 'AI-advies' : 'AI Consulting'}</div>
                    <Link to={`${prefix}/ai-consulting`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'AI-advies' : 'AI Consulting'}
                    </Link>
                    <Link to={`${prefix}/machine-learning`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      Machine Learning
                    </Link>
                    <div className="font-medium text-xs text-gray-500 px-4 py-2 mt-2">{isNL ? 'AI-oplossingen' : 'AI Solutions'}</div>
                    <Link to={`${prefix}/ai-automation-integration`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'AI-automatisering & Integratie' : 'AI Automation & Integration'}
                    </Link>
                    <Link to={`${prefix}/computer-vision`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Computer Vision Oplossingen' : 'Computer Vision Solutions'}
                    </Link>
                    <Link to={`${prefix}/conversational-ai`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      {isNL ? 'Conversationele AI-systemen' : 'Conversational AI Systems'}
                    </Link>
                  </div>
                )}
              </div>

              {/* Support */}
              <Link to={`${prefix}/support`} className="block px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg font-medium">
                {isNL ? 'Ondersteuning' : 'Support'}
              </Link>

              {/* About */}
              <Link to={`${prefix}/about`} className="block px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg font-medium">
                {isNL ? 'Over Ons' : 'About'}
              </Link>

              {/* Contact */}
              <Link to={`${prefix}/contact`} className="block px-4 py-3 text-gray-800 hover:bg-gray-50 rounded-lg font-medium">
                Contact
              </Link>

              {/* Mobile Actions */}
              <div className="pt-4 border-t border-gray-100 space-y-2">
                <a href="#" className="flex items-center px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-user-line"></i>
                  </div>
                  <span>Account</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}